/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 * 
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import EventEmitter from "./EventEmitter"
import Timer from "./Timer"

class FirebaseUtils_ {

    /** Number of active listeners */
    listenerCount = 0

    /** Wrap a listener, for analytics and debugging purposes */
    wrap(canceller) {

        // Increase count
        this.listenerCount += 1

        // Wrap canceller
        let hasCancelled = false
        return e => {

            // Only do once
            if (hasCancelled) return
            hasCancelled = true

            // Cancel it
            canceller()

            // Decrease count
            this.listenerCount -= 1

        }

    }

}

const FirebaseUtils = new FirebaseUtils_()
export default FirebaseUtils

/** 
 * Class which handles aggregating multiple Firebase queries into one big list, and rate-limits incomding changes as well. 
 * 
 * Example usage:
 * 
 * ```js
 * let aggregator = new FirestoreAggregator()
 * aggregator.addSource('src1', cb => firebase.firestore().collection('name').onSnapshot(cb))
 * aggregator.addEventListener('updated', onUpdatedList)
 * ```
 * 
 * @event updated Called when the contents of the 'items' list changes.
 */
export class FirestoreAggregator extends EventEmitter {

    /** @private Sources and their cancellers */
    sources = []

    /** All items */
    items = []

    /** Cached items */
    cachedItems = []
    cachedItemsUpdated = false

    /** Timestamp when the last update was received */
    lastUpdated = Date.now()

    /** Interval to run the update timer */
    updateTimerInterval = 2000

    /** @private If true, will do a full object check in the next iteration. Necessary when removing sources. */
    shouldDoFullCheckNextIteration = false

    /** @private Pending promise which is resolved as soon as any data is available */
    pendingPromise = new Promise(cb => this.pendingPromiseResolver = cb)

    /** 
     * User-defined function to check if an object has changed. If it has, newObject's fields will be merged into existingObject.
     * NOTE: If this object has not been seen before, existingObject will be null. In that case, this is just called as a way for
     * the user to potentially modify any fields in newObject before it's stored.
     */
    checkObjectHasChanged = (newObject, existingObject) => true

    /** Wait until any data has been returned */
    waitForSomeData() {
        if (this.items.length > 0) return Promise.resolve()
        else return this.pendingPromise
    }

    /** 
     * Add a source. If the source ID already is added, this is ignored. 
     * 
     * @param {string} id A unique ID for this source, to prevent duplicates
     * @param {function} creatorFunc A function which takes in a callback and creates the listener. Don't wrap it with FirebaseUtils.wrap(). Example: `cb => firebase.firestore().collection('name').onSnapshot(cb)`
     */
    addSource(id, creatorFunc) {

        // Check if exists already
        let source = this.sources.find(s => s.id == id)
        if (source)
            return

        // Create the listener
        this.sources.push({
            id,
            canceller: FirebaseUtils.wrap(creatorFunc(this.onSourceInput.bind(this, id))),
            items: [],
            itemIDs: {},
            hasChanges: false
        })
        
        // Update last update timestamp
        this.lastUpdated = Date.now()

    }

    /** @private Called when a source updates it's items */
    onSourceInput(id, snapshot) {

        // Update objects
        let source = this.sources.find(s => s.id == id)
        source.hasChanges = true

        // Get items
        if (snapshot.docs) {

            // This is a collection snapshot, multiple documents
            source.items = snapshot.docs.map(doc => Object.assign(doc.data(), { id: doc.id }))

        } else if (snapshot.id) {

            // This is a document snapshot, just a single document
            source.items = [
                Object.assign({}, snapshot.data(), { id: snapshot.id })
            ]

        } else {

            // Unknown snapshot listener type
            console.warn(`[Firestore Aggregator] Unknown snapshot data type returned by listener ${id}`)

        }

        // Store item IDs
        source.itemIDs = {}
        for (let item of source.items)
            source.itemIDs[item.id] = item

        // Remove items from the cache
        for (let i = 0 ; i < this.cachedItems.length ; i++) {
            if (source.itemIDs[this.cachedItems[i].id]) {
                this.cachedItems.splice(i--, 1)
                this.cachedItemsUpdated = true
            }
        }

        // Add the aggregate timer if necessary
        this.lastUpdated = Date.now()
        this.startTimer()

    }

    /**
     * Removes a source.
     */
    removeSource(id) {

        // Stop if source does not exist
        let source = this.sources.find(s => s.id == id)
        if (!source)
            return

        // Remove it
        source.canceller()
        this.sources = this.sources.filter(s => s != source)

        // Next iteration, force a full object check
        this.shouldDoFullCheckNextIteration = true

        // Add the aggregate timer if necessary
        this.lastUpdated = Date.now()
        this.startTimer()

    }

    /** Remove all sources */
    removeAllSources() {

        // Remove all sources
        for (let source of this.sources)
            this.removeSource(source.id)

    }

    /** @private Called often to check for changes from the listeners */
    onTimer() {

        // Check if we're already in the process for another thread. The loop function can take a long time when there is a lot of users,
        // so we try to split up the work by yielding every so often. When the loop yields, execution is halted until we call .next().
        if (this.timerLoopIterator) {
            let isComplete = this.timerLoopIterator.next().done
            if (isComplete) this.timerLoopIterator = null
            return
        }

        // Check if too soon to run again
        if (this.lastLoopRun && Date.now() - this.lastLoopRun < this.updateTimerInterval) return
        this.lastLoopRun = Date.now()

        // Start the iterator
        this.timerLoopIterator = this._onTimer()
        let isComplete = this.timerLoopIterator.next().done
        if (isComplete) 
            this.timerLoopIterator = null

    }

    * _onTimer() {

        // Check all that has changed
        let startTime = Date.now()
        let lastYieldTime = startTime
        let yieldCount = 0
        let addCount = 0
        let updateCount = 0
        let updatedSourceCount = 0
        for (let source of this.sources) {

            // Stop if no changes in this cell
            if (!source.hasChanges) continue
            source.hasChanges = false

            // Go through each item
            updatedSourceCount += 1
            for (let newItem of source.items) {

                // Yield if taking too long
                if (Date.now() - lastYieldTime > 30) {
                    yield false
                    lastYieldTime = Date.now()
                    yieldCount += 1
                }

                // Get existing user
                let existingItem = this.items.find(i => i.id == newItem.id)
                if (existingItem && this.checkObjectHasChanged(newItem, existingItem)) {

                    // Existing user has been updated
                    updateCount += 1
                    Object.assign(existingItem, newItem)

                } else if (!existingItem) {

                    // Inform about the update anyway
                    this.checkObjectHasChanged(newItem, null)

                    // A new user has been found
                    addCount += 1
                    this.items.push(newItem)

                } else {

                    // Item hasn't changed

                }

            }

        }

        // Check if cache source was updated
        // TODO: Horrible code duplication here...
        if (this.cachedItemsUpdated) {
            this.cachedItemsUpdated = false

            // Go through each item
            updatedSourceCount += 1
            for (let newItem of this.cachedItems) {

                // Yield if taking too long
                if (Date.now() - lastYieldTime > 30) {
                    yield false
                    lastYieldTime = Date.now()
                    yieldCount += 1
                }

                // Get existing user
                let existingItem = this.items.find(i => i.id == newItem.id)
                if (existingItem && this.checkObjectHasChanged(newItem, existingItem)) {

                    // Existing user has been updated
                    updateCount += 1
                    Object.assign(existingItem, newItem)

                } else if (!existingItem) {

                    // Inform about the update anyway
                    this.checkObjectHasChanged(newItem, null)

                    // A new user has been found
                    addCount += 1
                    this.items.push(newItem)

                } else {

                    // Item hasn't changed

                }

            }

        }

        // Check if nothing was updated
        if (updatedSourceCount == 0 && !this.shouldDoFullCheckNextIteration) {

            // Check if some time has passed since our last update
            if (Date.now() - this.lastUpdated > 1000 * 15) {

                // Cancel the timer, no more updates are coming through
                if (this.timer) Timer.clearInterval(this.timer)
                this.timer = null

            }

            // Stop here
            return

        }

        // Update last check interval
        this.shouldDoFullCheckNextIteration = false
        this.lastUpdated = Date.now()

        // Check for all that has been removed
        let removeCount = 0
        for (let i = 0 ; i < this.items.length ; i++) {

            // Yield if taking too long
            if (Date.now() - lastYieldTime > 30) {
                yield false
                lastYieldTime = Date.now()
                yieldCount += 1
            }

            // Find user
            let item = this.items[i]
            let foundItem = null
            for (let source of this.sources) {
                foundItem = source.itemIDs[item.id]
                if (foundItem) 
                    break
            }

            // Check if in the cache
            if (!foundItem)
                foundItem = this.cachedItems.find(itm => itm.id == item.id)

            // Stop if found
            if (foundItem)
                continue

            // This user has been removed
            this.items.splice(i, 1)
            removeCount += 1

        }

        // Log the changes
        let duration = Date.now() - startTime
        if (addCount || updateCount || removeCount || duration > 5)
            console.debug(`[Firebase Aggregator] Updated items: added=${addCount}, updated=${updateCount}, updated sources=${updatedSourceCount}, removed=${removeCount}, time=${duration}ms, yields=${yieldCount}`)

        // Notify listeners
        if (addCount || updateCount || removeCount)
            this.emit('updated', this.items)

        // Resolve the pending promise if necessary
        if (this.pendingPromiseResolver) {
            this.pendingPromiseResolver()
            this.pendingPromiseResolver = null
        }

    }

    /** Add an item to the cache */
    async addCacheDocument(id, docFetcher) {

        // Check if already added
        if (this.cachedItems.find(o => o.id == id))
            return console.debug(`[Firebase Aggregator] Skipped adding object to cache, it already exists. id=${id}`)

        // Fetch the doc
        let doc = await docFetcher()
        if (!doc.exists)
            throw new Error("The item did not exist.")

        // Get data
        let data = Object.assign(doc.data(), { id: doc.id })

        // Remove existing one
        for (let i = 0 ; i < this.cachedItems.length ; i++)
            if (this.cachedItems[i].id == data.id)
                this.cachedItems.splice(i--, 1)

        // Add it
        this.cachedItems.push(data)
        this.cachedItemsUpdated = true

        // Add the aggregate timer if necessary
        this.lastUpdated = Date.now()
        this.startTimer()

    }

    /** Used to start the timer */
    startTimer() {

        // Create timer if needed
        if (!this.timer)
            this.timer = Timer.setInterval(this.onTimer.bind(this), 50)
        
    }

}
